/**
 * <s-quick-card>: quick shop on a collection product card.
 * Nov 3 (merchant):
 * - The photos are ONE continuous circle: every color in the order the colors are listed, each color's
 *   front, back and close-up, then on to the next color. Past the last photo it keeps sliding onto the first.
 * - As the photos move into another color, that color's dot becomes the selected color (and the variant follows).
 * - Tapping a color dot jumps straight to that color, on the same view (front/back/close-up) the shopper was on.
 * - Swipe (finger, Mac two-finger trackpad, mouse drag) or the arrows; the shopper sets the pace, no forced pauses.
 * - The size menu picks the size; Add to cart submits through <s-product-form>.
 * - A tap on a photo without a swipe opens the product page.
 */
class SQuickCard extends HTMLElement {
  #ac;
  #slides = [];
  #entries = [];
  #index = 0;
  #color = '';
  #variants = [];
  #photos = [];
  #wrapTo = null;
  #wrapTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.media = this.querySelector('[data-quick-media]');
    this.track = this.querySelector('[data-quick-track]');
    this.link = this.querySelector('[data-quick-link]');
    this.nav = this.querySelector('[data-quick-nav]');
    this.prev = this.querySelector('[data-quick-prev]');
    this.next = this.querySelector('[data-quick-next]');
    this.buy = this.querySelector('[data-quick-buy]');
    this.idInput = this.querySelector('[data-quick-id]');
    this.size = this.querySelector('[data-quick-size]');
    this.add = this.querySelector('[data-quick-add]');
    this.error = this.querySelector('[data-add-to-cart-error]');

    // Each data block is read on its own, so one bad block never switches off swiping or the arrows.
    const read = (selector, fallback) => {
      try { return JSON.parse(this.querySelector(selector)?.textContent || fallback); } catch { return JSON.parse(fallback); }
    };
    const strip = read('[data-quick-galleries]', '[]');
    this.#variants = read('[data-quick-variants]', '[]');
    this.#photos = read('[data-quick-photos]', '[]');
    this.#entries = (Array.isArray(strip) ? strip : []).filter((entry) => Array.isArray(entry) && this.#photos[entry[1]]);

    this.colorIndex = Number(this.buy?.dataset.colorIndex ?? -1);
    this.sizeIndex = Number(this.buy?.dataset.sizeIndex ?? -1);

    const pressed = this.querySelector('[data-quick-color][aria-pressed="true"]');
    this.#color = pressed ? pressed.dataset.quickColor : (this.#entries[0]?.[0] || '');
    this.#buildStrip();
    this.#syncVariant();

    this.querySelectorAll('[data-quick-color]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.#selectColor(button.dataset.quickColor);
      }, opts);
    });

    this.colorSelect = this.querySelector('[data-quick-color-select]');
    this.colorSelect?.addEventListener('change', () => {
      this.#selectColor(this.colorSelect.value);
    }, opts);

    this.size?.addEventListener('change', () => {
      this.size.classList.remove('border-sale');
      this.#showError('');
      this.#syncVariant();
    }, opts);

    this.prev?.addEventListener('click', (event) => { event.preventDefault(); this.#warm(); this.#go(this.#index - 1); }, opts);
    this.next?.addEventListener('click', (event) => { event.preventDefault(); this.#warm(); this.#go(this.#index + 1); }, opts);

    // Capture phase runs before <s-product-form>, so a missing size or a sold-out choice never reaches the cart.
    this.addEventListener('submit', (event) => {
      if (this.size && !this.size.value) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.size.classList.add('border-sale');
        this.size.focus();
        this.#showError(this.buy.dataset.chooseSize);
        return;
      }
      if (this.add?.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal: this.#ac.signal });

    this.#bindSwipe(opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#wrapTimer);
  }

  // Shopper tapped a color: jump straight to that color, keeping the same view (front, back, close-up).
  #selectColor(color) {
    const first = this.#entries.findIndex((entry) => entry[0] === color);
    if (first >= 0 && this.#entries.length) {
      const currentFirst = this.#entries.findIndex((entry) => entry[0] === this.#entries[this.#index]?.[0]);
      const view = Math.max(0, this.#index - Math.max(currentFirst, 0));
      const count = this.#entries.filter((entry) => entry[0] === color).length;
      this.#warm();
      this.#go(first + Math.min(view, count - 1), true);
    }
    this.#setColor(color);
  }

  #setColor(color) {
    if (!color) return;
    this.#color = color;
    this.querySelectorAll('[data-quick-color]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.quickColor === color));
    });
    if (this.colorSelect && this.colorSelect.value !== color) this.colorSelect.value = color;
    this.#syncVariant();
  }

  #makeSlide(entry, sizes, alt) {
    const photo = this.#photos[entry[1]];
    const slide = document.createElement('div');
    slide.className = 'relative size-full shrink-0';
    slide.dataset.quickSlide = '';
    const img = document.createElement('img');
    img.src = photo.s;
    img.srcset = photo.ss;
    img.sizes = sizes;
    img.alt = alt;
    img.loading = this.warmed ? 'eager' : 'lazy';
    img.draggable = false;
    img.className = 'size-full object-cover object-center pointer-events-none';
    slide.append(img);
    return slide;
  }

  // Track layout: [copy of last] [every photo in order] [copy of first]. The copies make the loop seamless.
  #buildStrip() {
    if (!this.track) return;
    const serverSlide = this.track.querySelector('[data-quick-slide]');
    const serverImg = serverSlide?.querySelector('img');
    const sizes = serverImg?.getAttribute('sizes') || '50vw';
    const alt = serverImg?.getAttribute('alt') || '';
    const count = this.#entries.length;
    if (count < 2) {
      this.#slides = serverSlide ? [serverSlide] : [];
      if (this.nav) this.nav.hidden = true;
      this.#index = 0;
      this.track.style.transform = '';
      return;
    }
    let start = this.#entries.findIndex((entry) => entry[0] === this.#color);
    if (start < 0) start = 0;
    const real = this.#entries.map((entry, i) => {
      if (i === start && serverSlide) {
        if (serverImg) {
          serverImg.removeAttribute('fetchpriority');
          const photo = this.#photos[entry[1]];
          const fileOf = (url) => (url || '').split('?')[0].split('/').pop();
          if (photo && fileOf(serverImg.getAttribute('src')) !== fileOf(photo.s)) {
            serverImg.setAttribute('srcset', photo.ss);
            serverImg.setAttribute('src', photo.s);
          }
        }
        return serverSlide;
      }
      return this.#makeSlide(entry, sizes, alt);
    });
    const headCopy = this.#makeSlide(this.#entries[count - 1], sizes, alt);
    const tailCopy = this.#makeSlide(this.#entries[0], sizes, alt);
    headCopy.setAttribute('aria-hidden', 'true');
    tailCopy.setAttribute('aria-hidden', 'true');
    this.track.replaceChildren(headCopy, ...real, tailCopy);
    this.#slides = real;
    if (this.nav) this.nav.hidden = false;
    this.#go(start, true);
  }

  #position(pos, instant) {
    if (instant) this.track.style.transitionDuration = '0ms';
    this.track.style.transform = `translateX(${this.#direction() * -100 * pos}%)`;
    if (instant) {
      void this.track.offsetWidth;
      this.track.style.transitionDuration = '';
    }
  }

  // A wrap slides onto a copy, then silently swaps to the real photo. A new swipe finishes it right away.
  #finishWrap() {
    clearTimeout(this.#wrapTimer);
    if (this.#wrapTo === null) return;
    const pos = this.#wrapTo;
    this.#wrapTo = null;
    this.#position(pos, true);
  }

  #go(target, instant = false) {
    const count = this.#slides.length;
    if (count < 2) return;
    this.#finishWrap();
    let pos;
    if (target >= count) {
      this.#index = 0;
      pos = count + 1;
      if (!instant) {
        this.#wrapTo = 1;
        this.#wrapTimer = setTimeout(() => this.#finishWrap(), 320);
      } else pos = 1;
    } else if (target < 0) {
      this.#index = count - 1;
      pos = 0;
      if (!instant) {
        this.#wrapTo = count;
        this.#wrapTimer = setTimeout(() => this.#finishWrap(), 320);
      } else pos = count;
    } else {
      this.#index = target;
      pos = target + 1;
    }
    this.#position(pos, instant);
    const color = this.#entries[this.#index]?.[0];
    if (color && color !== this.#color) this.#setColor(color);
  }

  #direction() {
    return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
  }

  // Load this card's photos as soon as a shopper points at or touches it, so every slide and color is instant.
  #warm() {
    if (this.warmed) return;
    this.warmed = true;
    this.track?.querySelectorAll('img').forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
    });
  }

  // Nov 3 (merchant): the photos slide like turning a page, never just swap.
  // - Phone/tablet: a sideways finger swipe follows the finger; once it reads as sideways the page stops scrolling,
  //   and a pinch (two fingers) is always left to the browser so shoppers can zoom.
  // - Mac trackpad: a two-finger sideways swipe slides to the next photo.
  // - It spins like a circle: past the last photo it keeps sliding forward onto the first (and back from the first onto the last).
  #bindSwipe(opts) {
    if (!this.link || !this.track) return;
    this.link.style.touchAction = 'pan-y pinch-zoom';
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let mode = '';
    let dragged = false;
    let width = 1;

    this.media.addEventListener('pointerenter', () => this.#warm(), { ...opts, once: true });
    this.media.addEventListener('touchstart', () => this.#warm(), { ...opts, once: true, passive: true });

    const follow = (delta) => {
      this.#finishWrap();
      const max = width * 0.9;
      const clamped = Math.max(-max, Math.min(max, delta));
      this.track.style.transitionDuration = '0ms';
      this.track.style.transform = `translateX(${(-(this.#index + 1) * width + clamped) * this.#direction()}px)`;
    };

    const settle = (delta) => {
      this.track.style.transitionDuration = '';
      const threshold = Math.min(36, width * 0.12);
      const step = delta * this.#direction();
      if (step < -threshold) this.#go(this.#index + 1);
      else if (step > threshold) this.#go(this.#index - 1);
      else this.#go(this.#index);
    };

    // Touch: decide sideways vs. up/down from the first few pixels, leaning sideways, then hold that choice.
    this.link.addEventListener('touchstart', (event) => {
      if (this.#slides.length < 2) return;
      if (event.touches.length > 1) { if (mode === 'x') settle(0); mode = 'pinch'; return; }
      mode = '';
      dragged = false;
      dx = 0;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      width = this.media.clientWidth || 1;
    }, { ...opts, passive: true });

    this.link.addEventListener('touchmove', (event) => {
      if (mode === 'pinch' || mode === 'y' || this.#slides.length < 2) return;
      if (event.touches.length > 1) { if (mode === 'x') settle(0); mode = 'pinch'; return; }
      const t = event.touches[0];
      dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!mode) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        mode = Math.abs(dx) * 1.2 >= Math.abs(dy) ? 'x' : 'y';
        if (mode === 'y') return;
        dragged = true;
      }
      if (event.cancelable) event.preventDefault();
      follow(dx);
    }, { ...opts, passive: false });

    const touchEnd = (event) => {
      if (mode === 'x') settle(dx);
      if (!event.touches || event.touches.length === 0) mode = '';
    };
    this.link.addEventListener('touchend', touchEnd, opts);
    this.link.addEventListener('touchcancel', touchEnd, opts);

    // Mouse drag on desktop (touch is handled above).
    let mouseDown = false;
    this.link.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.#slides.length < 2) return;
      mouseDown = true;
      dragged = false;
      dx = 0;
      startX = event.clientX;
      width = this.media.clientWidth || 1;
    }, opts);
    this.link.addEventListener('pointermove', (event) => {
      if (!mouseDown) return;
      dx = event.clientX - startX;
      if (!dragged && Math.abs(dx) < 6) return;
      if (!dragged) { dragged = true; this.link.setPointerCapture?.(event.pointerId); }
      follow(dx);
    }, opts);
    const mouseUp = () => {
      if (!mouseDown) return;
      mouseDown = false;
      if (dragged) settle(dx);
    };
    this.link.addEventListener('pointerup', mouseUp, opts);
    this.link.addEventListener('pointercancel', mouseUp, opts);

    // Trackpad two-finger swipe: the photo follows the fingers, then slides on.
    // Nov 3 (merchant): no forced pause. Only the leftover coasting from the swipe that just landed is ignored;
    // a fresh swipe (fingers speed up again) or a swipe the other way is taken immediately, as fast as the shopper goes.
    let wheelX = 0;
    let wheelLocked = false;
    let lockDir = 0;
    let lastAbs = 0;
    let wheelTimer = 0;
    this.media.addEventListener('wheel', (event) => {
      if (this.#slides.length < 2) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      const abs = Math.abs(event.deltaX);
      const dir = Math.sign(event.deltaX);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        if (!wheelLocked && wheelX) settle(0);
        wheelX = 0;
        wheelLocked = false;
        lastAbs = 0;
      }, 60);
      if (wheelLocked) {
        const freshSwipe = dir !== lockDir || abs > lastAbs + 4;
        lastAbs = abs;
        if (!freshSwipe) return;
        wheelLocked = false;
        wheelX = 0;
      }
      lastAbs = abs;
      width = this.media.clientWidth || 1;
      wheelX -= event.deltaX;
      if (Math.abs(wheelX) > Math.min(60, width * 0.18)) {
        this.#warm();
        settle(wheelX);
        wheelLocked = true;
        lockDir = dir;
        wheelX = 0;
      } else {
        follow(wheelX);
      }
    }, { ...opts, passive: false });

    // A swipe should never also open the product page.
    this.link.addEventListener('click', (event) => {
      if (dragged) {
        event.preventDefault();
        dragged = false;
      }
    }, { capture: true, signal: opts.signal });
  }

  #matchVariant(color, size) {
    return this.#variants.filter((variant) => {
      if (this.colorIndex >= 0 && color && variant.o[this.colorIndex] !== color) return false;
      if (this.sizeIndex >= 0 && size && variant.o[this.sizeIndex] !== size) return false;
      return true;
    });
  }

  #syncVariant() {
    if (!this.#variants.length) return;
    const size = this.size ? this.size.value : '';

    if (this.size) {
      [...this.size.options].forEach((option) => {
        if (!option.value) return;
        const match = this.#matchVariant(this.#color, option.value);
        const available = match.some((variant) => variant.a);
        option.disabled = !available;
        if (!option.dataset.label) option.dataset.label = option.textContent;
        option.textContent = available ? option.dataset.label : `${option.dataset.label} \u2014 ${this.buy.dataset.soldOut}`;
      });
      if (size && this.size.selectedOptions[0]?.disabled) this.size.value = '';
    }

    const matches = this.#matchVariant(this.#color, this.size ? this.size.value : '');
    const variant = matches.find((v) => v.a) || matches[0];
    if (variant && this.idInput) this.idInput.value = String(variant.id);

    if (!this.add) return;
    const colorAvailable = this.#matchVariant(this.#color, '').some((v) => v.a);
    const chosenAvailable = this.size && this.size.value ? Boolean(variant?.a) : colorAvailable;
    this.add.textContent = chosenAvailable ? this.buy.dataset.add : this.buy.dataset.soldOut;
    if (chosenAvailable) {
      this.add.removeAttribute('aria-disabled');
      this.add.classList.remove('opacity-50');
    } else {
      this.add.setAttribute('aria-disabled', 'true');
      this.add.classList.add('opacity-50');
    }
  }

  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message || '';
    this.error.hidden = !message;
  }
}

if (!customElements.get('s-quick-card')) customElements.define('s-quick-card', SQuickCard);
